import React from 'react';
import type { AuthPhase } from '../../contexts/AuthContextTypes';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '../../contexts/useAuthContext';
import LoadingSpinner from './LoadingSpinner';

// BEM Block: messages
// Elements: __container, __title, __body, __actions, __spinner
// Modifiers: --full, --error, --loading

interface BaseMessageProps {
  title?: string;
  children?: React.ReactNode;
  fullScreen?: boolean;
  className?: string;
}

export const MessageContainer: React.FC<BaseMessageProps> = ({
  title,
  children,
  fullScreen = false,
  className = ''
}) => {
  const classes = [
    'messages__container',
    fullScreen ? 'messages__container--full' : '',
    className
  ].filter(Boolean).join(' ');
  return (
    <div className={classes}>
      {title && <h2 className="messages__title">{title}</h2>}
      <div className="messages__body">{children}</div>
    </div>
  );
};

export const LoadingMessage: React.FC<{ text?: string; fullScreen?: boolean }>= ({ text = 'Loading...', fullScreen=true }) => {
  // Debug: allow forcing the loading message via ?debugLoading
  const debugLoading = import.meta.env.DEV && new URLSearchParams(globalThis.location.search).has('debugLoading');
  
  if (debugLoading) {
    return (
      <MessageContainer fullScreen={fullScreen} className="messages__container--loading">
        <LoadingSpinner message={`${text} (DEBUG)`} fullHeight={false} />
      </MessageContainer>
    );
  }
  
  return (
    <MessageContainer fullScreen={fullScreen} className="messages__container--loading">
      <LoadingSpinner message={text} fullHeight={false} />
    </MessageContainer>
  );
}

const AUTH_PHASE_COPY: Record<AuthPhase, string> = {
  initializing: 'Preparing session…',
  signingIn: 'Logging in…',
  refreshing: 'Refreshing session…',
  signingOut: 'Logging out…',
  idle: 'Loading…'
};

export const AuthLoadingMessage: React.FC<{ phase?: AuthPhase }> = ({ phase = 'idle' }) => {
  // Debug: allow forcing specific auth loading phases via query params
  const debugAuthLoading = import.meta.env.DEV && new URLSearchParams(globalThis.location.search).has('debugAuthLoading');
  const debugPhase = new URLSearchParams(globalThis.location.search).get('debugPhase') as AuthPhase;
  
  if (debugAuthLoading) {
    const displayPhase = debugPhase || phase;
    const debugText = AUTH_PHASE_COPY[displayPhase] || 'Loading…';
    return <LoadingMessage text={`${debugText} (DEBUG)`} />;
  }
  
  const text = AUTH_PHASE_COPY[phase] || 'Loading…';
  return <LoadingMessage text={text} />;
};

export const ErrorMessage: React.FC<{ title?: string; message?: string; actionText?: string; onAction?: () => void; fullScreen?: boolean }>= ({
  title = 'Error',
  message = 'An unexpected error occurred.',
  actionText,
  onAction,
  fullScreen = true
}) => {
  const navigate = useNavigate();
  const handleAction = () => {
    if (onAction) onAction();
    else navigate('/login');
  };
  return (
    <MessageContainer title={title} fullScreen={fullScreen} className="messages__container--error">
      <p className="messages__text">{message}</p>
      {actionText && (
        <div className="messages__actions">
          <button className="messages__btn" onClick={handleAction}>{actionText}</button>
        </div>
      )}
    </MessageContainer>
  );
};

export const AuthErrorMessage: React.FC<{ error?: unknown }> = ({ error }) => {
  const navigate = useNavigate();
  let detail: string | undefined;
  if (error instanceof Error) detail = error.message;
  return (
    <ErrorMessage 
      title="Authentication Error" 
      message={detail ? `There was an error with authentication: ${detail}` : 'There was an error with authentication. Please try logging in again.'}
      actionText="Go to Login"
      onAction={() => navigate('/login')}
    />
  );
};

// Session timeout warning overlay
export const SessionTimeoutWarning: React.FC = () => {
  const { session } = useAuth0();
  
  // Debug: allow forcing the warning overlay via ?debugWarning
  const debugWarning = import.meta.env.DEV && new URLSearchParams(globalThis.location.search).has('debugWarning');
  
  if (debugWarning) {

    return (
      <div className="messages__overlay" role="dialog" aria-live="assertive" aria-label="Session timeout warning">
        <div className="messages__container messages__container--warning">
          <h2 className="messages__title">Session Expiring Soon (DEBUG)</h2>
          <p className="messages__text">You will be logged out in <strong>42</strong> seconds due to inactivity.</p>
          <div className="messages__actions">
            <button className="btn__primary" onClick={session.resetActivity}>Stay Signed In</button>
          </div>
        </div>
      </div>
    );
  }
  
  if (!session.warningActive || session.remainingSeconds == null) {
    return null;
  }
  const seconds = session.remainingSeconds;
  return (
    <div className="messages__overlay" role="dialog" aria-live="assertive" aria-label="Session timeout warning">
      <div className="messages__container messages__container--warning">
        <h2 className="messages__title">Session Expiring Soon</h2>
        <p className="messages__text">You will be logged out in <strong>{seconds}</strong> second{seconds === 1 ? '' : 's'} due to inactivity.</p>
        <div className="messages__actions">
          <button className="messages__btn" onClick={session.resetActivity}>Stay Signed In</button>
        </div>
      </div>
    </div>
  );
};

// Post-timeout informational message (e.g., on login screen)
export const SessionTimedOutMessage: React.FC = () => {
  const { session } = useAuth0();
  if (!session.timedOut) return null;
  return (
    <div className="messages__banner messages__banner--timeout" role="status" aria-live="polite">
      <p className="messages__text">You were logged out due to inactivity.</p>
    </div>
  );
};

export default {
  LoadingMessage,
  AuthLoadingMessage,
  ErrorMessage,
  AuthErrorMessage,
  SessionTimeoutWarning,
  SessionTimedOutMessage,
  MessageContainer
};
